import type { UseQueryExecute } from 'urql';
import type { DefaultParams, Params, RouteProps } from 'wouter';
import { type GetLedgerQuery, useGetLedgerQuery } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { createContext, useContext, useMemo } from 'react';

import type { KeyedRequired } from '@fragment/types';

type LedgerContextValue = {
  schemaKey?: string;
  ledgerId: string;
  ledger: GetLedgerQuery['ledger'];
  refreshGetLedgerQuery: UseQueryExecute;
};

const defaultContext: LedgerContextValue = {
  ledgerId: 'empty-ledger-id',
  ledger: null,
  refreshGetLedgerQuery: () => undefined,
};

export const LedgerContext = createContext<LedgerContextValue>(defaultContext);

/**
 * Only import this to mock a ledger in tests - otherwise, use the <LedgerProvider> component.
 */
export const LedgerProviderInternal = LedgerContext.Provider;

export type LedgerProviderProps<T extends DefaultParams> = {
  params: Params<T>;
  schemaKey?: string;
  children: RouteProps<T>['children'];
};

export const LedgerProvider = <T extends DefaultParams>({
  schemaKey,
  params,
  children,
}: LedgerProviderProps<T>) => {
  const { context: customerApiCtx, token } = useCustomerApiContext();
  const { ledgerId } = params;

  const variables = useMemo(() => ({ ledger: { id: ledgerId } }), [ledgerId]);

  const [{ data: ledger }, refreshGetLedgerQuery] = useGetLedgerQuery({
    variables,
    context: customerApiCtx,
    pause: !token,
  });

  const contextValue = useMemo(
    (): LedgerContextValue => ({
      ledger: ledger?.ledger,
      schemaKey,
      ledgerId,
      refreshGetLedgerQuery,
    }),
    [schemaKey, ledgerId, ledger?.ledger, refreshGetLedgerQuery]
  );

  const possibleElement =
    typeof children === 'function' ? children(params) : children;

  return (
    <LedgerContext.Provider value={contextValue}>
      {possibleElement}
    </LedgerContext.Provider>
  );
};

export const useLedger = () => useContext(LedgerContext);

/**
 * Use useSchemadLedger if you want to ensure that a schema key
 * exists for the Ledger in context.
 */
export const useSchemadLedger = (): KeyedRequired<
  LedgerContextValue,
  'schemaKey'
> => {
  const { schemaKey, ...rest } = useLedger();
  if (!schemaKey) {
    throw new Error('No schema key provided');
  }
  return { schemaKey, ...rest };
};

export const useLedgerStrict = (): Omit<LedgerContextValue, 'ledger'> & {
  ledger: NonNullable<LedgerContextValue['ledger']>;
} => {
  const { ledgerId, ledger, ...rest } = useLedger();
  if (!ledger) {
    throw new Error(`No ledger found for ledgerId ${ledgerId}`);
  }
  return {
    ledgerId,
    ledger,
    ...rest,
  };
};
